import { zodResolver } from '@hookform/resolvers/zod';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, Building2, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { ModeDot } from '@/components/ModeSwitcher';
import { usePortalAuth } from '@/lib/portal-auth/usePortalAuth';
import type { PortalAmbiguousClientHint, PortalLoginError } from '@/lib/portal-auth/types';
import { useMode } from '@/lib/mode/ModeProvider';
import type { OperationMode } from '@/lib/mode/setCurrentMode';

const LoginSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(8, 'Password must be at least 8 characters.'),
  mode: z.enum(['production', 'sandbox']),
});
type LoginForm = z.infer<typeof LoginSchema>;

export default function PortalLoginPage() {
  const { login, isAuthenticated, isBootstrapping } = usePortalAuth();
  const { mode: storedMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation() as { state: { from?: string } | null };

  const reduceMotion = useReducedMotion();

  const [apiError, setApiError] = useState<PortalLoginError | null>(null);
  const [pendingCredentials, setPendingCredentials] = useState<LoginForm | null>(null);
  const [clientHints, setClientHints] = useState<PortalAmbiguousClientHint[] | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '', mode: storedMode },
  });
  const selectedMode = watch('mode');

  useEffect(() => {
    if (!isBootstrapping && isAuthenticated) {
      navigate(location.state?.from ?? '/portal', { replace: true });
    }
  }, [isBootstrapping, isAuthenticated, location.state, navigate]);

  const submit = async (values: LoginForm, clientIdHint?: string) => {
    setApiError(null);
    const err = await login(values.email, values.password, clientIdHint);
    if (err === null) {
      // Persist the requested env so the chip + banner reflect intent
      // immediately. Phase B will extend the login endpoint with a `mode`
      // field and return 403 `auth.environment_forbidden` when mismatched.
      try {
        window.localStorage.setItem('dwpay.mode', values.mode);
      } catch {
        /* ignore */
      }
      navigate(location.state?.from ?? '/portal', { replace: true });
      return;
    }
    if (err.kind === 'ambiguous') {
      setPendingCredentials(values);
      setClientHints(err.hints);
      return;
    }
    setApiError(err);
  };

  const onSubmit = handleSubmit(async (values) => submit(values));

  if (!isBootstrapping && isAuthenticated) {
    return <Navigate to={location.state?.from ?? '/portal'} replace />;
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background p-8">
      <motion.div
        className="flex w-full max-w-sm flex-col items-center"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
      >
        <Logo className="mb-6 h-9" />

        {clientHints && pendingCredentials ? (
          <Card className="w-full">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-2 text-primary">
                <Building2 className="h-4 w-4" />
                <CardTitle>Which organisation?</CardTitle>
              </div>
              <CardDescription>This email has access at more than one client. Pick one to continue.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {clientHints.map((c) => (
                <Button
                  key={c.clientId}
                  variant="outline"
                  className="h-auto w-full justify-start py-3"
                  onClick={() => submit(pendingCredentials, c.clientId)}
                >
                  <div className="text-left">
                    <div className="font-medium">{c.clientName}</div>
                    <div className="font-mono text-xs text-muted-foreground">{c.clientCode}</div>
                  </div>
                </Button>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  setClientHints(null);
                  setPendingCredentials(null);
                }}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="w-full">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-2 text-primary">
                <Lock className="h-4 w-4" />
                <CardTitle>Portal sign-in</CardTitle>
              </div>
              <CardDescription>Partner portal — your client's data only.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    {...register('email')}
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    {...register('password')}
                    aria-invalid={!!errors.password}
                  />
                  {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
                </div>

                <EnvironmentRadios
                  selected={selectedMode}
                  onChange={(next) => setValue('mode', next, { shouldDirty: true })}
                />

                {apiError && apiError.kind !== 'ambiguous' && (
                  <div
                    role="alert"
                    className={
                      apiError.kind === 'lockout'
                        ? 'flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-200'
                        : 'flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive'
                    }
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>{apiError.message}</span>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting
                    ? 'Signing in…'
                    : `Sign in to ${selectedMode === 'sandbox' ? 'Sandbox' : 'Production'}`}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}

interface EnvironmentRadiosProps {
  selected: OperationMode;
  onChange: (next: OperationMode) => void;
}

/**
 * Mirror of the admin login's environment selector. See `Login/index.tsx`
 * for the rationale — Phase B will hide this for single-entitlement users.
 */
function EnvironmentRadios({ selected, onChange }: EnvironmentRadiosProps) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium text-muted-foreground">Environment</legend>
      <div className="grid grid-cols-2 gap-2">
        <EnvironmentRadio
          mode="production"
          label="Production"
          checked={selected === 'production'}
          onSelect={onChange}
        />
        <EnvironmentRadio
          mode="sandbox"
          label="Sandbox"
          checked={selected === 'sandbox'}
          onSelect={onChange}
        />
      </div>
    </fieldset>
  );
}

function EnvironmentRadio({
  mode,
  label,
  checked,
  onSelect,
}: {
  mode: OperationMode;
  label: string;
  checked: boolean;
  onSelect: (next: OperationMode) => void;
}) {
  return (
    <label
      className={
        checked
          ? 'flex cursor-pointer items-center gap-2 rounded-md border border-primary/70 bg-primary/5 px-3 py-2 text-sm font-medium text-foreground transition-colors'
          : 'flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/40'
      }
    >
      <input
        type="radio"
        name="portal-login-mode"
        value={mode}
        checked={checked}
        onChange={() => onSelect(mode)}
        className="sr-only"
      />
      <ModeDot mode={mode} />
      <span>{label}</span>
    </label>
  );
}
